import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { PageHeader } from "@/components/common/page-header";
import { TabShell } from "@/features/settings/components/tab-shell";
import {
  type EmailTemplate,
  listEmailTemplates,
} from "@/features/settings/email-templates-api";
import { cn } from "@/lib/utils";

/** Groups templates by `groupName`, preserving first-seen group order. */
function groupTemplates(list: EmailTemplate[]): [string, EmailTemplate[]][] {
  const groups = new Map<string, EmailTemplate[]>();
  for (const t of list) {
    const bucket = groups.get(t.groupName);
    if (bucket) bucket.push(t);
    else groups.set(t.groupName, [t]);
  }
  return [...groups.entries()];
}

export function EmailsTab() {
  const [list, setList] = useState<EmailTemplate[] | null>(null);
  const [error, setError] = useState(false);
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const [params] = useSearchParams();
  const src = params.get("src");

  useEffect(() => {
    let active = true;
    listEmailTemplates()
      .then((t) => active && setList(t))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  const groups = useMemo(() => (list ? groupTemplates(list) : []), [list]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Emails"
        subtitle="Automated emails your firm sends to clients (onboarding invites, portal notices, and more). Edit a template to customize its subject and body, or restore it to the default."
      />
      <TabShell loading={!list && !error} error={error}>
        {list && (
        <div className="flex flex-col gap-8">
          {groups.length === 0 ? (
            <p className="text-muted-foreground text-sm">No email templates yet.</p>
          ) : (
            groups.map(([groupName, templates]) => {
              const highlighted = !!src && groupName.toLowerCase() === src.toLowerCase();
              return (
                <section
                  key={groupName}
                  className={cn(
                    "flex flex-col gap-3",
                    highlighted && "-mx-2 rounded-lg bg-accent/40 px-2 py-2",
                  )}
                >
                  <h3 className="text-foreground text-sm font-semibold">{groupName}</h3>
                  <div className="divide-border divide-y rounded-lg border">
                    {templates.map((t) => (
                      <div
                        key={t.code}
                        className="flex items-center justify-between gap-4 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-foreground truncate text-sm font-medium">
                            {t.subject || t.code}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">{t.code}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <Badge variant={t.isCustomized ? "secondary" : "outline"}>
                            {t.isCustomized ? "Customized" : "Default"}
                          </Badge>
                          {isAdmin && (
                            <Button asChild size="sm" variant="ghost">
                              <Link to={`/settings/emails/${t.code}`}>Edit</Link>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      )}
      </TabShell>
    </div>
  );
}
